import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChevronLeft, ChevronRight, Printer, Download, Users, Calendar, User, Building2, BarChart3, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { format, startOfWeek, addDays, addWeeks, subWeeks, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, getDay, eachWeekOfInterval } from 'date-fns';
import { formatTime, formatTimeRange } from '../utils/time';
import { computeMergedHours } from '../utils/hours';
import logger from '../utils/logger';

const SERVICE_CONFIG = {
  day_program: { label: 'Explore Program', color: '#6B3FA0', bg: '#EDE7F6' },
  employment: { label: 'Employment', color: '#E65100', bg: '#FFF3E0' },
  in_home: { label: 'IHCS', color: '#1B5E20', bg: '#E8F5E9' },
  private_pay: { label: 'Private Pay', color: '#01579B', bg: '#E1F5FE' },
  on_call: { label: 'On-Call', color: '#B45309', bg: '#FEF3C7' },
};

const DAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAYS_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const MODE_CONFIG = {
  week: { label: 'Week Overview', icon: Calendar },
  person: { label: 'Per Person (Month)', icon: User },
  participant: { label: 'Per Participant (Month)', icon: Users },
  department: { label: 'Per Department (Month)', icon: Building2 },
  hours: { label: 'Staff Hours Report', icon: BarChart3 },
};

export default function PrintSchedule() {
  const { api, user } = useAuth();
  const [searchParams] = useSearchParams();
  const role = user?.role || 'staff';
  const isStaffOnly = !['administrator', 'supervisor', 'scheduler'].includes(role);
  const [mode, setMode] = useState(() => {
    if (isStaffOnly) return 'person';
    return searchParams.get('person') ? 'person' : 'week';
  });
  // Week mode
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [weekFilterService, setWeekFilterService] = useState('all');
  // Month mode (shared)
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [selectedService, setSelectedService] = useState('day_program');
  const [hoursFilterService, setHoursFilterService] = useState('all');
  const [staff, setStaff] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [selectedParticipantId, setSelectedParticipantId] = useState('');
  const [includeUnassigned, setIncludeUnassigned] = useState(false);

  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  const weekStart = format(currentWeek, 'yyyy-MM-dd');
  const monthStart = format(currentMonth, 'yyyy-MM-dd');
  const weekDates = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(currentWeek, i)), [currentWeek]);
  const monthDates = useMemo(() => eachDayOfInterval({ start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) }), [currentMonth]);

  const fetchStaff = useCallback(async () => {
    try {
      if (isStaffOnly) {
        // Staff can't list employees; just lock to themselves
        setStaff([{ id: user?.id, name: user?.name || 'Me' }]);
        setSelectedStaffId(user?.id);
        return;
      }
      const { data } = await api.get('/employees');
      const sorted = (data || []).filter(e => e.is_active !== false).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setStaff(sorted);
      try {
        const { data: parts } = await api.get('/participants');
        const psorted = (parts || []).filter(p => p.is_active !== false).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setParticipants(psorted);
        if (psorted.length) setSelectedParticipantId(prev => prev || psorted[0].id);
      } catch { /* participants list optional */ }
      const wantMe = searchParams.get('person') === 'me';
      if (wantMe && user?.id) {
        setSelectedStaffId(user.id);
      } else if (!selectedStaffId && sorted.length) {
        setSelectedStaffId(sorted[0].id);
      }
    } catch (e) { logger.error(e); }
  }, [api, selectedStaffId, isStaffOnly, user, searchParams]);

  const fetchShifts = useCallback(async () => {
    setLoading(true);
    try {
      const empParam = isStaffOnly && user?.id ? `&employee_id=${user.id}` : '';
      let data;
      if (mode === 'week') {
        ({ data } = await api.get(`/shifts?week_start=${weekStart}${empParam}`));
      } else {
        ({ data } = await api.get(`/shifts?month_start=${monthStart}${empParam}`));
      }
      // On-call assignments show on printed/monthly schedules as pseudo-shifts.
      let ocRows = [];
      try {
        const ocStart = mode === 'week' ? weekStart : monthStart;
        const ocEnd = mode === 'week'
          ? format(addDays(currentWeek, 6), 'yyyy-MM-dd')
          : format(endOfMonth(currentMonth), 'yyyy-MM-dd');
        const { data: oc } = await api.get(`/on-call?start=${ocStart}&end=${ocEnd}`);
        ocRows = (oc || [])
          .filter(o => o.employee_id && (!isStaffOnly || o.employee_id === user?.id))
          .map(o => ({
            id: `oncall-${o.date}`, date: o.date,
            time_in: o.time_in || '09:00', time_out: o.time_out || '17:00',
            service_line: 'on_call', employee_id: o.employee_id, employee_name: o.employee_name,
            participants: [], location: '', notes: o.notes || 'On-call coverage',
            group_number: null, status: 'scheduled',
          }));
      } catch { /* on-call optional */ }
      setShifts([...(data || []), ...ocRows]);
    } catch (e) { logger.error('Print fetch error:', e); }
    setLoading(false);
  }, [api, mode, weekStart, monthStart, currentWeek, currentMonth, isStaffOnly, user]);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);
  useEffect(() => { fetchShifts(); }, [fetchShifts]);

  const handlePrint = () => window.print();

  const handleExportCSV = () => {
    let rows, filename;
    const baseHeaders = ['Date', 'Day', 'Service Line', 'Staff', 'Time In', 'Time Out', 'Hours', 'Location', 'Participants', 'Notes', 'Group'];
    const rowsFor = (list) => {
      const out = [baseHeaders];
      list.sort((a, b) => a.date.localeCompare(b.date) || a.time_in.localeCompare(b.time_in)).forEach(s => {
        const dayName = DAYS_FULL[new Date(s.date + 'T12:00:00').getDay()];
        const [ih, im] = s.time_in.split(':').map(Number);
        const [oh, om] = s.time_out.split(':').map(Number);
        const hrs = ((oh + om/60) - (ih + im/60)).toFixed(2);
        out.push([s.date, dayName, SERVICE_CONFIG[s.service_line]?.label || s.service_line, s.employee_name, formatTime(s.time_in), formatTime(s.time_out), hrs, s.location || '', (s.participants || []).join('; '), s.notes || '', s.group_number || '']);
      });
      return out;
    };
    if (mode === 'week') {
      const filtered = weekFilterService === 'all' ? shifts : shifts.filter(s => s.service_line === weekFilterService);
      rows = rowsFor(dedupeShifts(filtered));
      filename = `TNSP_Week_${weekStart}.csv`;
    } else if (mode === 'person') {
      const staffName = staff.find(s => s.id === selectedStaffId)?.name || 'Staff';
      const filtered = shifts.filter(s => s.employee_id === selectedStaffId);
      rows = rowsFor(dedupeShifts(filtered));
      filename = `TNSP_${staffName.replace(/\s+/g, '_')}_${format(currentMonth, 'yyyy-MM')}.csv`;
    } else if (mode === 'participant') {
      const pName = selectedParticipant?.name || 'Participant';
      rows = rowsFor([...participantShifts]);
      filename = `TNSP_${pName.replace(/\s+/g, '_')}_${format(currentMonth, 'yyyy-MM')}.csv`;
    } else if (mode === 'hours') {
      const filteredShifts = hoursFilterService === 'all' ? shifts : shifts.filter(s => s.service_line === hoursFilterService);
      const monthStr = format(currentMonth, 'yyyy-MM');
      const weeks = buildMonthWeeks(currentMonth);
      const weekHeaders = weeks.map((w, i) => `Wk ${i + 1} (${format(w.start, 'M/d')}-${format(w.end, 'M/d')})`);
      rows = [['Staff', 'Weekly Cap', ...weekHeaders, `${format(currentMonth, 'MMMM yyyy')} Total`, 'Cap × Weeks in Month', '% Utilization']];
      const activeStaff = staff.filter(s => s.is_active !== false);
      activeStaff.forEach(s => {
        const empShifts = filteredShifts.filter(x => x.employee_id === s.id);
        const weekTotals = weeks.map(w => {
          const wShifts = empShifts.filter(x => x.date >= format(w.start, 'yyyy-MM-dd') && x.date <= format(w.end, 'yyyy-MM-dd'));
          return computeMergedHours(wShifts);
        });
        const monthTotal = weekTotals.reduce((a, b) => a + b, 0);
        const cap = s.max_weekly_hours || 40;
        const capMonth = cap * weeks.length;
        const util = capMonth > 0 ? (monthTotal / capMonth) * 100 : 0;
        rows.push([s.name, cap.toFixed(1), ...weekTotals.map(h => h.toFixed(2)), monthTotal.toFixed(2), capMonth.toFixed(1), `${util.toFixed(0)}%`]);
      });
      filename = `TNSP_Staff_Hours_${monthStr}${hoursFilterService !== 'all' ? '_' + hoursFilterService : ''}.csv`;
    } else {
      const filtered = shifts.filter(s => s.service_line === selectedService);
      rows = rowsFor(dedupeShifts(filtered));
      filename = `TNSP_${SERVICE_CONFIG[selectedService].label.replace(/\s+/g, '_')}_${format(currentMonth, 'yyyy-MM')}.csv`;
    }
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  // Deduplicate a list of shifts by (date, employee_id, service_line, time_in, time_out).
  // Same person + same service + same time on same day → ONE row (merging group #s + participants).
  const dedupeShifts = (list, withUnassigned = includeUnassigned) => {
    const merged = new Map();
    const unassignedRows = [];
    for (const s of list) {
      if (!s.employee_id) {
        unassignedRows.push(s);
        continue;
      }
      const key = `${s.date}|${s.employee_id}|${s.service_line}|${s.time_in}|${s.time_out}`;
      const existing = merged.get(key);
      if (existing) {
        if (s.group_number && !String(existing.group_number || '').split(',').map(g=>g.trim()).includes(String(s.group_number))) {
          existing.group_number = `${existing.group_number || ''}${existing.group_number ? ', ' : ''}${s.group_number}`;
        }
        const existingP = new Set(existing.participants || []);
        (s.participants || []).forEach(p => existingP.add(p));
        existing.participants = Array.from(existingP);
      } else {
        merged.set(key, { ...s });
      }
    }
    const result = Array.from(merged.values());
    if (withUnassigned) result.push(...unassignedRows);
    return result;
  };

  const getShiftsForDay = (date, serviceLine) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayShifts = shifts.filter(s =>
      s.date === dateStr &&
      (serviceLine === 'all' || s.service_line === serviceLine)
    );
    return dedupeShifts(dayShifts).sort((a, b) => {
      const ag = parseInt(String(a.group_number || '99').split(',')[0], 10) || 99;
      const bg = parseInt(String(b.group_number || '99').split(',')[0], 10) || 99;
      return ag - bg || (a.time_in || '').localeCompare(b.time_in || '');
    });
  };

  const selectedStaff = staff.find(s => s.id === selectedStaffId);
  const selectedParticipant = participants.find(p => p.id === selectedParticipantId);

  // Shifts this participant is on (matched by name against shift.participants), month-bounded
  const participantShifts = useMemo(() => {
    if (!selectedParticipant?.name) return [];
    const pname = selectedParticipant.name.trim().toLowerCase();
    const mStart = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
    const mEnd = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
    return dedupeShifts(
      shifts.filter(s =>
        s.date >= mStart && s.date <= mEnd &&
        (s.participants || []).some(p => (p || '').trim().toLowerCase() === pname)
      ),
      true // participant cares about the slot even if staff not yet assigned
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shifts, selectedParticipant, currentMonth]);

  // Google Calendar handoff: .ics file the family can import into the
  // participant's own calendar in two clicks.
  const handleExportICS = () => {
    if (!selectedParticipant || participantShifts.length === 0) return;
    const stamp = format(new Date(), "yyyyMMdd'T'HHmmss");
    const events = participantShifts.map(s => {
      const d = s.date.replace(/-/g, '');
      const t = (hm) => (hm || '00:00').replace(':', '') + '00';
      const staffTxt = s.employee_name ? ` with ${s.employee_name}` : ' (staff TBD)';
      const svc = SERVICE_CONFIG[s.service_line]?.label || s.service_line;
      return [
        'BEGIN:VEVENT',
        `UID:${s.id}@tnsprograms`,
        `DTSTAMP:${stamp}`,
        `DTSTART:${d}T${t(s.time_in)}`,
        `DTEND:${d}T${t(s.time_out)}`,
        `SUMMARY:TNS ${svc}${staffTxt}`,
        s.location ? `LOCATION:${String(s.location).replace(/[,;]/g, ' ')}` : '',
        `DESCRIPTION:${svc} session for ${selectedParticipant.name}${s.group_number ? ` · Group ${s.group_number}` : ''}`,
        'END:VEVENT',
      ].filter(Boolean).join('\r\n');
    });
    const ics = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//TNS Programs//WorkHub//EN', ...events, 'END:VCALENDAR'].join('\r\n');
    const blob = new Blob([ics], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `TNSP_${selectedParticipant.name.replace(/\s+/g, '_')}_${format(currentMonth, 'yyyy-MM')}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 sm:p-8 space-y-6" data-testid="print-schedule">
      {/* Controls - hidden in print */}
      <div className="flex items-center justify-between flex-wrap gap-4 print:hidden">
        <div>
          <h1 className="text-4xl tracking-tight font-extrabold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Print / Export
          </h1>
          <p className="text-base text-[#5A4D6E] mt-1">Pick a view below, then Print or Export CSV.</p>
        </div>
        <div className="flex items-center gap-2">
          {mode === 'participant' && (
            <Button onClick={handleExportICS} variant="outline"
              disabled={!selectedParticipant || participantShifts.length === 0}
              className="border-[#DED8EB] text-[#2D1B4E] gap-2" data-testid="export-ics-btn"
              title="Download a calendar file to import into the participant's Google Calendar">
              <Calendar className="h-4 w-4" /> Google Calendar (.ics)
            </Button>
          )}
          <Button onClick={handleExportCSV} variant="outline" className="border-[#DED8EB] text-[#2D1B4E] gap-2" data-testid="export-csv-btn">
            <Download className="h-4 w-4" /> Export CSV
          </Button>
          <Button onClick={handlePrint} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-2" data-testid="print-btn">
            <Printer className="h-4 w-4" /> Print
          </Button>
        </div>
      </div>

      {/* Mode selector - hidden in print and for staff-only users */}
      {!isStaffOnly && (
        <div className="print:hidden flex items-center gap-2 bg-[#F0EDF6] border border-[#DED8EB] rounded-lg p-1 w-fit">
          {Object.entries(MODE_CONFIG).map(([k, { label, icon: Icon }]) => (
            <button key={k} onClick={() => setMode(k)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                mode === k ? 'bg-white text-[#2D1B4E] shadow-sm' : 'text-[#8E85A0] hover:text-[#2D1B4E]'
              }`} data-testid={`print-mode-${k}`}>
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>
      )}

      {/* Mode-specific filters - hidden in print */}
      <div className="print:hidden flex items-center gap-3 flex-wrap">
        {mode === 'week' && (
          <>
            <Select value={weekFilterService} onValueChange={setWeekFilterService}>
              <SelectTrigger className="w-48 border-[#DED8EB] bg-white" data-testid="week-filter-service"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Service Lines</SelectItem>
                <SelectItem value="day_program">Explore Program</SelectItem>
                <SelectItem value="employment">Employment</SelectItem>
                <SelectItem value="in_home">IHCS</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))} className="border-[#DED8EB]" data-testid="print-prev-week"><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentWeek(startOfWeek(new Date(), { weekStartsOn: 0 }))} className="border-[#DED8EB] text-[#2D1B4E]">Today</Button>
              <Button variant="outline" size="icon" onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))} className="border-[#DED8EB]" data-testid="print-next-week"><ChevronRight className="h-4 w-4" /></Button>
              <span className="text-sm font-semibold text-[#2D1B4E] ml-2" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                {format(currentWeek, 'MMM d')} - {format(addDays(currentWeek, 6), 'MMM d, yyyy')}
              </span>
            </div>
          </>
        )}
        {mode === 'person' && (
          <>
            {isStaffOnly ? (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#EDE7F6] border border-[#9B7ED8]">
                <User className="h-4 w-4 text-[#6B3FA0]" />
                <span className="text-sm font-semibold text-[#2D1B4E]">{user?.name || 'Me'}</span>
              </div>
            ) : (
              <Select value={selectedStaffId} onValueChange={setSelectedStaffId}>
                <SelectTrigger className="w-64 border-[#DED8EB] bg-white" data-testid="person-select"><SelectValue placeholder="Pick a staff member" /></SelectTrigger>
                <SelectContent className="max-h-[320px]">
                  {staff.map(s => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                </SelectContent>
              </Select>
            )}
            <MonthNav currentMonth={currentMonth} setCurrentMonth={setCurrentMonth} />
          </>
        )}
        {mode === 'participant' && !isStaffOnly && (
          <>
            <Select value={selectedParticipantId} onValueChange={setSelectedParticipantId}>
              <SelectTrigger className="w-64 border-[#DED8EB] bg-white" data-testid="participant-select"><SelectValue placeholder="Pick a participant" /></SelectTrigger>
              <SelectContent className="max-h-[320px]">
                {participants.map(p => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
              </SelectContent>
            </Select>
            <MonthNav currentMonth={currentMonth} setCurrentMonth={setCurrentMonth} />
          </>
        )}
        {mode === 'department' && !isStaffOnly && (
          <>
            <Select value={selectedService} onValueChange={setSelectedService}>
              <SelectTrigger className="w-48 border-[#DED8EB] bg-white" data-testid="department-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="day_program">Explore Program</SelectItem>
                <SelectItem value="employment">Employment</SelectItem>
                <SelectItem value="in_home">IHCS</SelectItem>
              </SelectContent>
            </Select>
            <MonthNav currentMonth={currentMonth} setCurrentMonth={setCurrentMonth} />
          </>
        )}
        {mode === 'hours' && !isStaffOnly && (
          <>
            <Select value={hoursFilterService} onValueChange={setHoursFilterService}>
              <SelectTrigger className="w-48 border-[#DED8EB] bg-white" data-testid="hours-filter-service"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Service Lines</SelectItem>
                <SelectItem value="day_program">Explore Program</SelectItem>
                <SelectItem value="employment">Employment</SelectItem>
                <SelectItem value="in_home">IHCS</SelectItem>
                <SelectItem value="private_pay">Private Pay</SelectItem>
              </SelectContent>
            </Select>
            <MonthNav currentMonth={currentMonth} setCurrentMonth={setCurrentMonth} />
          </>
        )}

        {!isStaffOnly && (
          <label className="flex items-center gap-2 text-sm text-[#5A4D6E] cursor-pointer ml-auto" data-testid="print-include-unassigned-label">
            <input
              type="checkbox"
              checked={includeUnassigned}
              onChange={(e) => setIncludeUnassigned(e.target.checked)}
              className="h-4 w-4 accent-[#6B3FA0]"
              data-testid="print-include-unassigned"
            />
            Include unassigned slots
          </label>
        )}
      </div>

      {/* Printable content */}
      <div className="print:p-0">
        {loading ? (
          <div className="text-center py-12 text-[#8E85A0]">Loading schedule...</div>
        ) : mode === 'week' ? (
          <WeekView weekDates={weekDates} currentWeek={currentWeek} filterService={weekFilterService} getShiftsForDay={getShiftsForDay} />
        ) : mode === 'person' ? (
          <PersonMonthView staffMember={selectedStaff} shifts={dedupeShifts(shifts.filter(s => s.employee_id === selectedStaffId))} monthDates={monthDates} currentMonth={currentMonth} />
        ) : mode === 'participant' ? (
          <ParticipantMonthView participant={selectedParticipant} shifts={participantShifts} monthDates={monthDates} currentMonth={currentMonth} />
        ) : mode === 'hours' ? (
          <StaffHoursReport staff={staff} shifts={shifts} currentMonth={currentMonth} filterService={hoursFilterService} />
        ) : (
          <DepartmentMonthView service={selectedService} shifts={dedupeShifts(shifts.filter(s => s.service_line === selectedService))} monthDates={monthDates} currentMonth={currentMonth} />
        )}
      </div>
    </div>
  );
}

function MonthNav({ currentMonth, setCurrentMonth }) {
  return (
    <div className="flex items-center gap-1">
      <Button variant="outline" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="border-[#DED8EB]" data-testid="print-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
      <Button variant="outline" size="sm" onClick={() => setCurrentMonth(startOfMonth(new Date()))} className="border-[#DED8EB] text-[#2D1B4E]">This Month</Button>
      <Button variant="outline" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="border-[#DED8EB]" data-testid="print-next-month"><ChevronRight className="h-4 w-4" /></Button>
      <span className="text-sm font-semibold text-[#2D1B4E] ml-2" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
        {format(currentMonth, 'MMMM yyyy')}
      </span>
    </div>
  );
}

function PrintHeader({ title, subtitle }) {
  return (
    <div className="hidden print:block mb-4">
      <h1 className="text-2xl font-bold text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h1>
      <p className="text-sm text-gray-600">{subtitle}</p>
    </div>
  );
}

function calcHours(s) {
  const [ih, im] = s.time_in.split(':').map(Number);
  const [oh, om] = s.time_out.split(':').map(Number);
  return (oh + om / 60) - (ih + im / 60);
}

// ====== WEEK VIEW (existing) ======
function WeekView({ weekDates, currentWeek, filterService, getShiftsForDay }) {
  return (
    <>
      <PrintHeader
        title="The Next Step Programs — Weekly Schedule"
        subtitle={`Week of ${format(currentWeek, 'MMMM d')} - ${format(addDays(currentWeek, 6), 'MMMM d, yyyy')}${filterService !== 'all' ? ` · ${SERVICE_CONFIG[filterService]?.label}` : ''}`}
      />
      <div className="space-y-4">
        {weekDates.map(date => {
          const dayShifts = getShiftsForDay(date, filterService);
          if (dayShifts.length === 0 && filterService !== 'all') return null;
          const dateStr = format(date, 'yyyy-MM-dd');
          return (
            <div key={dateStr} className="border border-[#DED8EB] rounded-lg overflow-hidden bg-white print:border-gray-300 print:break-inside-avoid" data-testid={`print-day-${dateStr}`}>
              <div className="bg-[#F0EDF6] px-4 py-2 border-b border-[#DED8EB] print:bg-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-[#2D1B4E] print:text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>{format(date, 'EEEE, MMMM d')}</h3>
                  <span className="text-xs text-[#8E85A0] flex items-center gap-1 print:text-gray-500"><Users className="h-3 w-3" /> {dayShifts.length} shifts</span>
                </div>
              </div>
              <div className="p-3">
                {dayShifts.length === 0 ? (
                  <p className="text-sm text-[#8E85A0] text-center py-2 print:text-gray-400">No shifts scheduled</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[#8E85A0] text-xs uppercase tracking-wider print:text-gray-500">
                        {filterService === 'all' && <th className="text-left py-1 pr-3 font-semibold">Service</th>}
                        <th className="text-left py-1 pr-3 font-semibold">Staff</th>
                        <th className="text-left py-1 pr-3 font-semibold">Time</th>
                        <th className="text-left py-1 pr-3 font-semibold">Location</th>
                        <th className="text-left py-1 font-semibold">Participants</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dayShifts.map(shift => (
                        <tr key={shift.id} className="border-t border-[#DED8EB] print:border-gray-200">
                          {filterService === 'all' && (
                            <td className="py-1.5 pr-3">
                              <Badge className={`text-[10px] ${
                                shift.service_line === 'day_program' ? 'bg-[#EDE7F6] text-[#6B3FA0]' :
                                shift.service_line === 'employment' ? 'bg-[#FFF3E0] text-[#E65100]' :
                                'bg-[#E8F5E9] text-[#1B5E20]'
                              } print:bg-transparent print:border`}>
                                {SERVICE_CONFIG[shift.service_line]?.label}
                                {shift.group_number && ` G${shift.group_number}`}
                              </Badge>
                            </td>
                          )}
                          <td className="py-1.5 pr-3 font-medium text-[#2D1B4E] print:text-black">{shift.employee_name || '— Unassigned —'}</td>
                          <td className="py-1.5 pr-3 text-[#5A4D6E] print:text-gray-700" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{formatTimeRange(shift.time_in, shift.time_out)}</td>
                          <td className="py-1.5 pr-3 text-[#5A4D6E] print:text-gray-600">{shift.location || '-'}</td>
                          <td className="py-1.5 text-[#5A4D6E] print:text-gray-600">{(shift.participants || []).join(', ') || shift.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ====== PERSON MONTH VIEW ======
function PersonMonthView({ staffMember, shifts, monthDates, currentMonth }) {
  // Dedupe overlapping participant rows: one staff covering multiple
  // participants in the same time block on the same day = one block of hours.
  const totalHours = useMemo(() => computeMergedHours(shifts), [shifts]);
  const byService = useMemo(() => {
    const grouped = {};
    for (const s of shifts) {
      (grouped[s.service_line] = grouped[s.service_line] || []).push(s);
    }
    const out = {};
    for (const [sl, list] of Object.entries(grouped)) out[sl] = computeMergedHours(list);
    return out;
  }, [shifts]);
  const firstDay = getDay(startOfMonth(currentMonth));

  return (
    <>
      <PrintHeader
        title={`Monthly Schedule — ${staffMember?.name || 'Staff'}`}
        subtitle={`${format(currentMonth, 'MMMM yyyy')} · ${shifts.length} shifts · ${totalHours.toFixed(1)} hours`}
      />
      {!staffMember ? (
        <p className="text-sm text-[#8E85A0] py-8 text-center">Pick a staff member above.</p>
      ) : (
        <div className="space-y-5">
          {/* Summary card */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 print:break-inside-avoid">
            <SummaryCard label="Total Shifts" value={shifts.length} />
            <SummaryCard label="Total Hours" value={totalHours.toFixed(1)} />
            <SummaryCard label="Days Worked" value={new Set(shifts.map(s => s.date)).size} />
            <SummaryCard label="Avg / Shift" value={shifts.length ? (totalHours / shifts.length).toFixed(1) : '0.0'} suffix="h" />
          </div>

          {/* Service line breakdown */}
          {Object.keys(byService).length > 0 && (
            <div className="flex flex-wrap gap-2">
              {Object.entries(byService).map(([sl, h]) => (
                <Badge key={sl} className={`text-xs ${
                  sl === 'day_program' ? 'bg-[#EDE7F6] text-[#6B3FA0]' :
                  sl === 'employment' ? 'bg-[#FFF3E0] text-[#E65100]' :
                  'bg-[#E8F5E9] text-[#1B5E20]'
                } print:bg-transparent print:border`}>
                  {SERVICE_CONFIG[sl]?.label}: {h.toFixed(1)}h
                </Badge>
              ))}
            </div>
          )}

          {/* Month calendar grid */}
          <div className="border border-[#DED8EB] rounded-lg overflow-hidden bg-white print:border-gray-300 print:break-inside-avoid" data-testid="person-month-grid">
            <div className="grid grid-cols-7 bg-[#F0EDF6] border-b border-[#DED8EB] print:bg-gray-100">
              {DAYS_FULL.map((d, i) => (
                <div key={d} className="px-2 py-1.5 text-[10px] font-bold text-[#2D1B4E] uppercase tracking-wider text-center print:text-black">{DAYS_SHORT[i]} <span className="hidden md:inline">{d.slice(0, 3)}</span></div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {Array.from({ length: firstDay }).map((_, i) => (
                <div key={`pad-${i}`} className="h-24 border-r border-b border-[#F0EDF6]" />
              ))}
              {monthDates.map(date => {
                const dateStr = format(date, 'yyyy-MM-dd');
                const dayShifts = shifts.filter(s => s.date === dateStr);
                const dayHours = dayShifts.reduce((a, s) => a + calcHours(s), 0);
                return (
                  <div key={dateStr} className="h-24 border-r border-b border-[#F0EDF6] p-1 overflow-hidden print:border-gray-200">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs font-bold text-[#2D1B4E] print:text-black">{format(date, 'd')}</span>
                      {dayHours > 0 && <span className="text-[9px] font-mono text-[#6B3FA0] print:text-gray-600">{dayHours.toFixed(1)}h</span>}
                    </div>
                    {dayShifts.map(s => (
                      <div key={s.id} className={`text-[9px] leading-tight mb-0.5 px-1 py-0.5 rounded print:border ${
                        s.service_line === 'day_program' ? 'bg-[#EDE7F6] text-[#6B3FA0]' :
                        s.service_line === 'employment' ? 'bg-[#FFF3E0] text-[#E65100]' :
                        'bg-[#E8F5E9] text-[#1B5E20]'
                      }`} style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                        {formatTime(s.time_in)}–{formatTime(s.time_out)}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Detailed list */}
          <div className="border border-[#DED8EB] rounded-lg overflow-hidden bg-white print:border-gray-300">
            <div className="bg-[#F0EDF6] px-4 py-2 border-b border-[#DED8EB] print:bg-gray-100">
              <h3 className="text-sm font-bold text-[#2D1B4E] print:text-black">Shift Detail</h3>
            </div>
            {shifts.length === 0 ? (
              <p className="text-sm text-[#8E85A0] text-center py-6">No shifts this month.</p>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="text-[#8E85A0] text-xs uppercase tracking-wider print:text-gray-500 bg-[#F7F4EF] print:bg-transparent">
                  <th className="text-left px-3 py-1.5 font-semibold">Date</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Service</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Time</th>
                  <th className="text-right px-3 py-1.5 font-semibold">Hours</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Location</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Participants</th>
                </tr></thead>
                <tbody>
                  {[...shifts].sort((a, b) => a.date.localeCompare(b.date) || a.time_in.localeCompare(b.time_in)).map(s => (
                    <tr key={s.id} className="border-t border-[#F0EDF6] print:border-gray-200">
                      <td className="px-3 py-1.5 font-mono text-[#2D1B4E] print:text-black">{format(new Date(s.date + 'T12:00:00'), 'EEE, MMM d')}</td>
                      <td className="px-3 py-1.5">
                        <Badge className={`text-[10px] ${
                          s.service_line === 'day_program' ? 'bg-[#EDE7F6] text-[#6B3FA0]' :
                          s.service_line === 'employment' ? 'bg-[#FFF3E0] text-[#E65100]' :
                          'bg-[#E8F5E9] text-[#1B5E20]'
                        } print:bg-transparent print:border`}>{SERVICE_CONFIG[s.service_line]?.label}</Badge>
                      </td>
                      <td className="px-3 py-1.5 text-[#5A4D6E] print:text-gray-700" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{formatTimeRange(s.time_in, s.time_out)}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-[#5A4D6E] print:text-gray-700">{calcHours(s).toFixed(2)}</td>
                      <td className="px-3 py-1.5 text-[#5A4D6E] print:text-gray-600">{s.location || '—'}</td>
                      <td className="px-3 py-1.5 text-[#5A4D6E] print:text-gray-600">{(s.participants || []).join(', ') || '—'}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-[#DED8EB] bg-[#F7F4EF] print:bg-transparent font-bold">
                    <td className="px-3 py-1.5 text-[#2D1B4E]" colSpan={3}>TOTAL · {shifts.length} shifts · {new Set(shifts.map(s => s.date)).size} days</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[#2D1B4E]">{totalHours.toFixed(2)}</td>
                    <td colSpan={2} />
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ====== PARTICIPANT MONTH VIEW ======
function ParticipantMonthView({ participant, shifts, monthDates, currentMonth }) {
  const totalHours = useMemo(() => computeMergedHours(shifts.filter(s => s.employee_id)), [shifts]);
  const staffNames = useMemo(() => Array.from(new Set(shifts.map(s => s.employee_name).filter(Boolean))), [shifts]);
  const firstDay = getDay(startOfMonth(currentMonth));
  const svcBadge = (sl) =>
    sl === 'day_program' ? 'bg-[#EDE7F6] text-[#6B3FA0]' :
    sl === 'employment' ? 'bg-[#FFF3E0] text-[#E65100]' :
    sl === 'private_pay' ? 'bg-[#E1F5FE] text-[#01579B]' :
    'bg-[#E8F5E9] text-[#1B5E20]';

  return (
    <>
      <PrintHeader
        title={`Participant Schedule — ${participant?.name || 'Participant'}`}
        subtitle={`${format(currentMonth, 'MMMM yyyy')} · ${shifts.length} sessions · ${totalHours.toFixed(1)} hours`}
      />
      {!participant ? (
        <p className="text-sm text-[#8E85A0] py-8 text-center">Pick a participant above.</p>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 print:break-inside-avoid">
            <SummaryCard label="Sessions" value={shifts.length} />
            <SummaryCard label="Total Hours" value={totalHours.toFixed(1)} />
            <SummaryCard label="Days Scheduled" value={new Set(shifts.map(s => s.date)).size} />
            <SummaryCard label="Staff Involved" value={staffNames.length} />
          </div>

          {staffNames.length > 0 && (
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs font-semibold text-[#8E85A0] uppercase tracking-wider">Staff:</span>
              {staffNames.map(n => (
                <Badge key={n} className="text-xs bg-[#F0EDF6] text-[#5A4D6E] print:bg-transparent print:border">{n}</Badge>
              ))}
            </div>
          )}

          {/* Month calendar grid */}
          <div className="border border-[#DED8EB] rounded-lg overflow-hidden bg-white print:border-gray-300 print:break-inside-avoid" data-testid="participant-month-grid">
            <div className="grid grid-cols-7 bg-[#F0EDF6] border-b border-[#DED8EB] print:bg-gray-100">
              {DAYS_FULL.map((d, i) => (
                <div key={d} className="px-2 py-1.5 text-[10px] font-bold text-[#2D1B4E] uppercase tracking-wider text-center print:text-black">{DAYS_SHORT[i]} <span className="hidden md:inline">{d.slice(0, 3)}</span></div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {Array.from({ length: firstDay }).map((_, i) => (
                <div key={`pad-${i}`} className="h-24 border-r border-b border-[#F0EDF6]" />
              ))}
              {monthDates.map(date => {
                const dateStr = format(date, 'yyyy-MM-dd');
                const dayShifts = shifts.filter(s => s.date === dateStr).sort((a, b) => (a.time_in || '').localeCompare(b.time_in || ''));
                return (
                  <div key={dateStr} className="h-24 border-r border-b border-[#F0EDF6] p-1 overflow-hidden print:border-gray-200">
                    <span className="text-xs font-bold text-[#2D1B4E] print:text-black">{format(date, 'd')}</span>
                    {dayShifts.map(s => (
                      <div key={s.id} className={`text-[9px] leading-tight mb-0.5 px-1 py-0.5 rounded print:border ${svcBadge(s.service_line)}`}
                        style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                        title={`${formatTimeRange(s.time_in, s.time_out)} · ${s.employee_name || 'Staff TBD'}`}>
                        {formatTime(s.time_in)} {(s.employee_name || 'TBD').split(/\s+/)[0]}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Detailed list */}
          <div className="border border-[#DED8EB] rounded-lg overflow-hidden bg-white print:border-gray-300">
            <div className="bg-[#F0EDF6] px-4 py-2 border-b border-[#DED8EB] print:bg-gray-100">
              <h3 className="text-sm font-bold text-[#2D1B4E] print:text-black">Session Detail</h3>
            </div>
            {shifts.length === 0 ? (
              <p className="text-sm text-[#8E85A0] text-center py-6">No scheduled sessions this month.</p>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="text-[#8E85A0] text-xs uppercase tracking-wider print:text-gray-500 bg-[#F7F4EF] print:bg-transparent">
                  <th className="text-left px-3 py-1.5 font-semibold">Date</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Service</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Time</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Staff</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Group</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Location</th>
                </tr></thead>
                <tbody>
                  {[...shifts].sort((a, b) => a.date.localeCompare(b.date) || (a.time_in || '').localeCompare(b.time_in || '')).map(s => (
                    <tr key={s.id} className="border-t border-[#F0EDF6] print:border-gray-200" data-testid={`participant-shift-row-${s.id}`}>
                      <td className="px-3 py-1.5 font-mono text-[#2D1B4E] print:text-black">{format(new Date(s.date + 'T12:00:00'), 'EEE, MMM d')}</td>
                      <td className="px-3 py-1.5">
                        <Badge className={`text-[10px] ${svcBadge(s.service_line)} print:bg-transparent print:border`}>{SERVICE_CONFIG[s.service_line]?.label || s.service_line}</Badge>
                      </td>
                      <td className="px-3 py-1.5 text-[#5A4D6E] print:text-gray-700" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{formatTimeRange(s.time_in, s.time_out)}</td>
                      <td className="px-3 py-1.5 text-[#5A4D6E] print:text-gray-700">{s.employee_name || <span className="italic text-[#E65100]">Staff TBD</span>}</td>
                      <td className="px-3 py-1.5 text-[#5A4D6E] print:text-gray-600">{s.group_number || '—'}</td>
                      <td className="px-3 py-1.5 text-[#5A4D6E] print:text-gray-600">{s.location || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ====== DEPARTMENT MONTH VIEW ======
function DepartmentMonthView({ service, shifts, monthDates, currentMonth }) {
  const cfg = SERVICE_CONFIG[service];
  // Dedupe per staff per day across multiple participant rows.
  const byStaff = useMemo(() => {
    const grouped = {};
    for (const s of shifts) {
      const name = s.employee_name || '— Unassigned —';
      (grouped[name] = grouped[name] || []).push(s);
    }
    return Object.entries(grouped).map(([name, list]) => ({
      name,
      shifts: list.length,
      hours: computeMergedHours(list),
      days: new Set(list.map(s => s.date)),
    })).sort((a, b) => b.hours - a.hours);
  }, [shifts]);
  const totalHours = useMemo(() => byStaff.reduce((a, s) => a + s.hours, 0), [byStaff]);

  return (
    <>
      <PrintHeader
        title={`Monthly Schedule — ${cfg.label}`}
        subtitle={`${format(currentMonth, 'MMMM yyyy')} · ${shifts.length} shifts · ${totalHours.toFixed(1)} hours · ${byStaff.length} staff`}
      />
      <div className="space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 print:break-inside-avoid">
          <SummaryCard label="Total Shifts" value={shifts.length} />
          <SummaryCard label="Total Hours" value={totalHours.toFixed(1)} />
          <SummaryCard label="Staff Used" value={byStaff.length} />
          <SummaryCard label="Days Active" value={new Set(shifts.map(s => s.date)).size} />
        </div>

        {/* Day-by-day grouping */}
        <div className="space-y-3">
          {monthDates.map(date => {
            const dateStr = format(date, 'yyyy-MM-dd');
            const dayShifts = shifts.filter(s => s.date === dateStr).sort((a, b) => a.time_in.localeCompare(b.time_in));
            if (dayShifts.length === 0) return null;
            return (
              <div key={dateStr} className="border border-[#DED8EB] rounded-lg overflow-hidden bg-white print:border-gray-300 print:break-inside-avoid">
                <div className="bg-[#F0EDF6] px-4 py-1.5 border-b border-[#DED8EB] print:bg-gray-100 flex items-center justify-between">
                  <h3 className="text-sm font-bold text-[#2D1B4E] print:text-black">{format(date, 'EEEE, MMMM d')}</h3>
                  <span className="text-xs text-[#8E85A0] print:text-gray-500">{dayShifts.length} shift{dayShifts.length !== 1 ? 's' : ''}</span>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {dayShifts.map(s => (
                      <tr key={s.id} className="border-t border-[#F0EDF6] print:border-gray-200">
                        <td className="px-3 py-1 font-medium text-[#2D1B4E] print:text-black">{s.employee_name || <span className="text-[#D9534F] italic">Unassigned</span>}</td>
                        <td className="px-3 py-1 text-[#5A4D6E] print:text-gray-700" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{formatTimeRange(s.time_in, s.time_out)}</td>
                        <td className="px-3 py-1 font-mono text-[#5A4D6E] text-right print:text-gray-700">{calcHours(s).toFixed(2)}h</td>
                        <td className="px-3 py-1 text-[#5A4D6E] print:text-gray-600">{s.location || '—'}</td>
                        <td className="px-3 py-1 text-[#5A4D6E] print:text-gray-600">{(s.participants || []).join(', ') || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
          {shifts.length === 0 && <p className="text-sm text-[#8E85A0] text-center py-6">No shifts for this department in {format(currentMonth, 'MMMM yyyy')}.</p>}
        </div>

        {/* Staff totals at bottom */}
        {byStaff.length > 0 && (
          <div className="border border-[#DED8EB] rounded-lg overflow-hidden bg-white print:border-gray-300 print:break-inside-avoid">
            <div className="bg-[#F0EDF6] px-4 py-2 border-b border-[#DED8EB] print:bg-gray-100">
              <h3 className="text-sm font-bold text-[#2D1B4E] print:text-black">Staff Totals</h3>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="text-[#8E85A0] text-xs uppercase tracking-wider bg-[#F7F4EF] print:bg-transparent print:text-gray-500">
                <th className="text-left px-3 py-1.5 font-semibold">Staff</th>
                <th className="text-right px-3 py-1.5 font-semibold">Shifts</th>
                <th className="text-right px-3 py-1.5 font-semibold">Days</th>
                <th className="text-right px-3 py-1.5 font-semibold">Hours</th>
              </tr></thead>
              <tbody>
                {byStaff.map(st => (
                  <tr key={st.name} className="border-t border-[#F0EDF6] print:border-gray-200">
                    <td className="px-3 py-1.5 font-medium text-[#2D1B4E] print:text-black">{st.name}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[#5A4D6E] print:text-gray-700">{st.shifts}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[#5A4D6E] print:text-gray-700">{st.days.size}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[#2D1B4E] font-bold print:text-black">{st.hours.toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-[#DED8EB] bg-[#F7F4EF] print:bg-transparent font-bold">
                  <td className="px-3 py-1.5 text-[#2D1B4E]">TOTAL</td>
                  <td className="px-3 py-1.5 text-right font-mono">{shifts.length}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{new Set(shifts.map(s => s.date)).size}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{totalHours.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function SummaryCard({ label, value, suffix = '' }) {
  return (
    <div className="border border-[#DED8EB] rounded-lg p-3 bg-white print:border-gray-300">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#8E85A0] print:text-gray-500">{label}</p>
      <p className="text-xl font-extrabold text-[#2D1B4E] print:text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}{suffix}</p>
    </div>
  );
}


// ====== STAFF HOURS REPORT ======
// Build the weeks (Sun–Sat) that intersect the selected month. Any partial
// weeks at the start/end are included but only days that fall in the month
// contribute hours (we clip each week to the month boundary when totaling).
function buildMonthWeeks(currentMonth) {
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const weekStarts = eachWeekOfInterval({ start: monthStart, end: monthEnd }, { weekStartsOn: 0 });
  return weekStarts.map(ws => {
    const we = addDays(ws, 6);
    // Clip to month boundaries so hours from prior/next month aren't counted.
    const start = ws < monthStart ? monthStart : ws;
    const end = we > monthEnd ? monthEnd : we;
    return { start, end, rawStart: ws, rawEnd: we };
  });
}

function StaffHoursReport({ staff, shifts, currentMonth, filterService }) {
  const activeStaff = useMemo(
    () => (staff || []).filter(s => s.is_active !== false),
    [staff]
  );
  const weeks = useMemo(() => buildMonthWeeks(currentMonth), [currentMonth]);
  const filteredShifts = useMemo(
    () => filterService === 'all' ? shifts : shifts.filter(s => s.service_line === filterService),
    [shifts, filterService]
  );

  const rows = useMemo(() => {
    return activeStaff.map(s => {
      const empShifts = filteredShifts.filter(x => x.employee_id === s.id);
      const weekTotals = weeks.map(w => {
        const startStr = format(w.start, 'yyyy-MM-dd');
        const endStr = format(w.end, 'yyyy-MM-dd');
        const wShifts = empShifts.filter(x => x.date >= startStr && x.date <= endStr);
        return computeMergedHours(wShifts);
      });
      const monthTotal = weekTotals.reduce((a, b) => a + b, 0);
      const cap = s.max_weekly_hours || 40;
      const capMonth = cap * weeks.length;
      const util = capMonth > 0 ? (monthTotal / capMonth) * 100 : 0;
      const overCapWeeks = weekTotals.filter(h => h > cap + 0.01).length;
      return { id: s.id, name: s.name, cap, weekTotals, monthTotal, capMonth, util, overCapWeeks };
    });
  }, [activeStaff, filteredShifts, weeks]);

  const scheduled = rows.filter(r => r.monthTotal > 0);
  const unscheduled = rows.filter(r => r.monthTotal === 0);
  const grandTotal = rows.reduce((sum, r) => sum + r.monthTotal, 0);
  const totalCap = rows.reduce((sum, r) => sum + r.capMonth, 0);
  const overCapCount = rows.filter(r => r.overCapWeeks > 0).length;

  // Sortable columns. Default = name ascending, scheduled staff first.
  // 'default' preserves the original scheduled-then-unscheduled grouping;
  // any explicit sort applies uniformly across all rows.
  const [sortKey, setSortKey] = useState('default');
  const [sortDir, setSortDir] = useState('desc');
  const toggleSort = (key) => {
    if (sortKey !== key) {
      // Numeric columns default to desc (highest first — spot overload fast);
      // name column defaults to asc (A→Z).
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
      return;
    }
    // Same column: cycle through the natural directions, then back to default.
    if (key === 'name') {
      if (sortDir === 'asc') setSortDir('desc');
      else { setSortKey('default'); setSortDir('asc'); }
    } else {
      if (sortDir === 'desc') setSortDir('asc');
      else { setSortKey('default'); setSortDir('desc'); }
    }
  };
  const sortedRows = useMemo(() => {
    if (sortKey === 'default') return [...scheduled, ...unscheduled];
    const arr = [...rows];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let av, bv;
      if (sortKey === 'name') { av = a.name || ''; bv = b.name || ''; return av.localeCompare(bv) * dir; }
      if (sortKey === 'cap') { av = a.cap; bv = b.cap; }
      else if (sortKey === 'month') { av = a.monthTotal; bv = b.monthTotal; }
      else if (sortKey === 'util') { av = a.util; bv = b.util; }
      else return 0;
      if (av === bv) return (a.name || '').localeCompare(b.name || '');
      return (av - bv) * dir;
    });
    return arr;
  }, [rows, scheduled, unscheduled, sortKey, sortDir]);

  const renderSortIcon = (colKey) => {
    if (sortKey !== colKey) return <ArrowUpDown className="inline h-3 w-3 ml-1 text-[#C4BDD1]" />;
    return sortDir === 'asc'
      ? <ArrowUp className="inline h-3 w-3 ml-1 text-[#6B3FA0]" />
      : <ArrowDown className="inline h-3 w-3 ml-1 text-[#6B3FA0]" />;
  };
  const headerCls = (colKey) => `cursor-pointer select-none hover:text-[#6B3FA0] transition-colors ${sortKey === colKey ? 'text-[#6B3FA0]' : ''}`;

  const utilPill = (util) => {
    if (util >= 100) return 'bg-red-100 text-[#D9534F] border-red-300';
    if (util >= 85) return 'bg-yellow-50 text-[#B45309] border-yellow-300';
    if (util >= 40) return 'bg-green-50 text-[#2E7D32] border-green-300';
    if (util > 0) return 'bg-blue-50 text-[#01579B] border-blue-200';
    return 'bg-gray-50 text-[#8E85A0] border-gray-200';
  };

  return (
    <>
      <PrintHeader
        title="The Next Step Programs — Staff Hours Report"
        subtitle={`${format(currentMonth, 'MMMM yyyy')}${filterService !== 'all' ? ` · ${SERVICE_CONFIG[filterService]?.label}` : ' · All service lines'}`}
      />
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 print:mb-3">
        <SummaryCard label="Staff Scheduled" value={scheduled.length} suffix={` / ${rows.length}`} />
        <SummaryCard label="Total Hours" value={grandTotal.toFixed(1)} suffix="h" />
        <SummaryCard label="Utilization" value={totalCap > 0 ? ((grandTotal / totalCap) * 100).toFixed(0) : '0'} suffix="%" />
        <SummaryCard label="Over-Cap Staff" value={overCapCount} />
      </div>

      <div className="border border-[#DED8EB] rounded-lg overflow-x-auto bg-white print:border-gray-300" data-testid="staff-hours-report">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F0EDF6] print:bg-gray-100 text-[#8E85A0] text-[11px] uppercase tracking-wider print:text-gray-600 border-b border-[#DED8EB]">
              <th className={`text-left px-3 py-2 font-semibold sticky left-0 bg-[#F0EDF6] print:bg-gray-100 ${headerCls('name')}`}
                  onClick={() => toggleSort('name')} data-testid="hours-sort-name">
                Staff{renderSortIcon('name')}
              </th>
              <th className={`text-right px-2 py-2 font-semibold ${headerCls('cap')}`}
                  onClick={() => toggleSort('cap')} data-testid="hours-sort-cap">
                Cap /wk{renderSortIcon('cap')}
              </th>
              {weeks.map((w, i) => (
                <th key={i} className="text-right px-2 py-2 font-semibold whitespace-nowrap">
                  Wk {i + 1}
                  <div className="text-[9px] font-normal text-[#8E85A0] normal-case tracking-normal" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                    {format(w.start, 'M/d')}–{format(w.end, 'M/d')}
                  </div>
                </th>
              ))}
              <th className={`text-right px-3 py-2 font-semibold bg-[#EDE7F6] print:bg-gray-200 ${headerCls('month')}`}
                  onClick={() => toggleSort('month')} data-testid="hours-sort-month">
                Month{renderSortIcon('month')}
              </th>
              <th className={`text-right px-3 py-2 font-semibold bg-[#EDE7F6] print:bg-gray-200 ${headerCls('util')}`}
                  onClick={() => toggleSort('util')} data-testid="hours-sort-util">
                % Cap{renderSortIcon('util')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map(r => (
              <tr key={r.id} className="border-t border-[#F0EDF6] print:border-gray-200 hover:bg-[#F7F4EF] print:hover:bg-transparent" data-testid={`hours-row-${r.id}`}>
                <td className="px-3 py-1.5 font-medium text-[#2D1B4E] print:text-black sticky left-0 bg-white print:bg-white">
                  {r.name}
                  {r.overCapWeeks > 0 && (
                    <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wider text-[#D9534F] bg-red-50 border border-red-300 px-1 py-0.5 rounded">
                      {r.overCapWeeks} wk over cap
                    </span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-right text-[#8E85A0] font-mono print:text-gray-600">{r.cap.toFixed(0)}h</td>
                {r.weekTotals.map((h, i) => (
                  <td key={i} className={`px-2 py-1.5 text-right font-mono ${
                    h === 0 ? 'text-[#C4BDD1] print:text-gray-400' :
                    h > r.cap + 0.01 ? 'text-[#D9534F] font-bold print:text-black' :
                    h > r.cap * 0.85 ? 'text-[#B45309]' : 'text-[#5A4D6E] print:text-gray-700'
                  }`}>
                    {h > 0 ? h.toFixed(1) : '—'}
                  </td>
                ))}
                <td className="px-3 py-1.5 text-right font-mono font-bold text-[#2D1B4E] bg-[#F0EDF6]/50 print:bg-transparent print:text-black">
                  {r.monthTotal > 0 ? r.monthTotal.toFixed(1) : '—'}
                </td>
                <td className="px-3 py-1.5 text-right bg-[#F0EDF6]/50 print:bg-transparent">
                  {r.monthTotal > 0 ? (
                    <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${utilPill(r.util)} print:border-gray-300 print:bg-transparent`}>
                      {r.util.toFixed(0)}%
                    </span>
                  ) : <span className="text-[#C4BDD1]">—</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={weeks.length + 4} className="text-center py-6 text-[#8E85A0]">No staff to report.</td></tr>
            )}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-[#DED8EB] bg-[#F0EDF6] print:bg-gray-100 font-bold">
              <td className="px-3 py-2 text-[#2D1B4E] print:text-black sticky left-0 bg-[#F0EDF6] print:bg-gray-100">TOTAL</td>
              <td className="px-2 py-2 text-right font-mono text-[#8E85A0]">—</td>
              {weeks.map((_, i) => {
                const weekSum = rows.reduce((sum, r) => sum + r.weekTotals[i], 0);
                return <td key={i} className="px-2 py-2 text-right font-mono text-[#2D1B4E] print:text-black">{weekSum.toFixed(1)}</td>;
              })}
              <td className="px-3 py-2 text-right font-mono text-[#2D1B4E] print:text-black bg-[#EDE7F6] print:bg-gray-200">{grandTotal.toFixed(1)}</td>
              <td className="px-3 py-2 text-right font-mono text-[#2D1B4E] print:text-black bg-[#EDE7F6] print:bg-gray-200">
                {totalCap > 0 ? ((grandTotal / totalCap) * 100).toFixed(0) : 0}%
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="text-[11px] text-[#8E85A0] mt-2 print:text-gray-500">
        Hours are computed with overlap dedupe — one staff covering multiple participants/groups in the same time block counts once.
        Week columns clip to the calendar month. % Cap = month total ÷ (weekly cap × weeks in month).
      </p>
    </>
  );
}
